import { defineComponent, type PropType } from 'vue'

/**
 * Table cell component
 *
 * @component CATableCell
 *
 * @prop {number} [columnSpan=1] - The number of columns the cell should span.
 * @prop {boolean} [hidden=false] - Whether the cell is hidden.
 * @prop {boolean} [right=false] - Whether the cell content should be right-aligned.
 * @prop {string} [showBreakpoint='medium'] - The breakpoint at which the cell will be shown.
 * @prop {boolean} [wrap=false] - Whether the cell should wrap text.
 *
 * @slot default - The content of the cell
 */

export default defineComponent({
	name: 'CATableCell',

	props: {
		/**
		 *
		 * The number of columns the cell should span
		 * @default 1
		 * @type {number}
		 */
		columnSpan: { default: 1, type: Number },
		/**
		 * Whether the cell is hidden
		 * @default false
		 * @type {boolean}
		 */
		hidden: { default: false, type: Boolean },
		/**
		 * Whether the cell content should be right-aligned
		 * @default false
		 * @type {boolean}
		 */
		right: { default: false, type: Boolean },
		/**
		 * The breakpoint at which the cell will be shown
		 * @default 'medium'
		 * @type {string}
		 * @enum {'large' | 'medium' | 'small' | 'x-large' | 'x-small'}
		 */
		showBreakpoint: { default: 'medium', type: String as PropType<'large' | 'medium' | 'small' | 'x-large' | 'x-small'> },
		/**
		 * Whether the cell should wrap text
		 * @default false
		 * @type {boolean}
		 */
		wrap: { default: false, type: Boolean },
	},

	computed: {
		breakpoint(): string {
			return `${this.showBreakpoint}:table-cell`
		},
	},

	render() {
		return (
			<td
				class={{
					'wrap-td': this.wrap,
					[this.breakpoint]: this.hidden,
					hidden: this.hidden,
					right: this.right,
				}}
				colspan={this.columnSpan}
				data-test="ca-table-cell"
			>
				{this.$slots.default?.()}
			</td>
		)
	},
})
